import {
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter, forwardRef,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  TemplateRef
} from '@angular/core';
import {Subscription} from 'rxjs';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {BytesPipe} from '@ee/common/pipes';

@Component({
  selector: 'ee-upload-selector',
  template: `
    <div class="upload-wrapper">
      <div class="dropzone" [ngClass]="getDropzoneClass()">
        <input [id]="id" class="dz" type="file" #fileInputEl [attr.accept]="accept" [multiple]="false" [disabled]="disable" />

        <!-- Row 1: Icon (always same position) -->
        <div class="row-icon">
          @if (uploadState === 'uploading') {
            <mat-icon class="upload" fontSet="fa" fontIcon="fa-arrow-up-from-bracket"></mat-icon>
          } @else if (uploadState === 'scanning') {
            <mat-spinner class="upload-spinner" diameter="24"></mat-spinner>
          } @else if (uploadState === 'ready') {
            <mat-icon class="upload" fontSet="fa" fontIcon="fa-shield-check"></mat-icon>
          } @else if (uploadState === 'infected') {
            <mat-icon class="upload" fontSet="fa" fontIcon="fa-shield-xmark"></mat-icon>
          } @else if (uploadState === 'rejected') {
            <mat-icon class="upload" fontSet="fa" fontIcon="fa-triangle-exclamation"></mat-icon>
          } @else if (uploadState === 'failed') {
            <mat-icon class="upload" fontSet="fa" fontIcon="fa-rotate-right"></mat-icon>
          } @else if (hasError) {
            <mat-icon class="upload" fontSet="fa" fontIcon="fa-file-xmark"></mat-icon>
          } @else if (files.length) {
            <mat-icon class="upload" fontSet="fa" fontIcon="fa-file-check"></mat-icon>
          } @else {
            <mat-icon class="upload" fontSet="fa" fontIcon="fa-file-arrow-up"></mat-icon>
          }
        </div>

        <!-- Row 2: Filename or instructions (always same position).
             Error states take precedence over the filename so the user sees WHY
             the upload was refused, not just a red tile with their filename. -->
        <div class="row-text">
          @if (uploadState === 'infected') {
            <span class="status-line state-error-text">Malware detected</span>
          } @else if (uploadState === 'rejected') {
            <span class="status-line state-error-text">{{ statusReason || 'File rejected' }}</span>
          } @else if (uploadState === 'failed') {
            <span class="status-line state-error-text">{{ statusReason || 'Upload failed' }} — click to retry</span>
          } @else if (files.length && files[0]) {
            <span class="filename">{{ getFileName(files[0]) }}</span>
          } @else if (uploadState === 'uploading') {
            <span class="status-line">Uploading… {{ progressPercent ?? 0 }}%</span>
          } @else if (uploadState === 'scanning') {
            <span class="status-line">Scanning for malware…</span>
          } @else if (uploadState === 'ready') {
            <span class="status-line state-ready-text">Ready</span>
          } @else {
            <!--
              A caller can replace this idle prompt by passing a template through promptTemplate; the default
              below stands for every existing usage.

              A TemplateRef rather than ng-content: content cannot be re-projected through two component levels,
              so the inner select stopped matching and the fallback rendered while the caller's prompt was
              silently dropped. A template reference passes down as an ordinary input and cannot go quiet.
              (No backticks in here: this comment lives inside a template literal.)
            -->
            <span class="hint-text">
              @if (promptTemplate) {
                <ng-container *ngTemplateOutlet="promptTemplate"></ng-container>
              } @else {
                Drop file or <span class="browse-link">browse</span>
              }
            </span>
          }
        </div>

        <!-- Row 3: Progress bar only (no button) -->
        <div class="row-action">
          @if (uploadState === 'uploading') {
            <mat-progress-bar class="progress-bar" mode="determinate" [value]="progressPercent ?? 0"></mat-progress-bar>
          }
        </div>
      </div>
    </div>
    `,
  styles: [`
    :host {
      min-width: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .upload-wrapper {
      display: flex;
      flex-direction: row;
      justify-content: stretch;
      align-items: stretch;
      min-width: 0;
      overflow: hidden;
      flex: 1;

      .dz {
        display: none;
      }

      .dropzone {
        width: 100%;
        max-width: 100%;
        text-align: center;
        border: 4px dashed var(--primary-color);
        position: relative;
        cursor: pointer;
        background-color: var(--color-surface);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 4px;
        padding: 0 8px;
        box-sizing: border-box;
        min-height: 80px;
        flex: 1;
        border-radius: 4px;
        overflow: hidden;

        /*
          The full-screen variant, for a drop zone that IS the screen rather than a control on a form:
          "Step 2 of 2 · Upload your file" and anything like it. Taller, a lighter 2px rule, and a larger
          neutral icon, per the approved mockup.

          A variant rather than the callers reaching in with ::ng-deep. The dropzone and icon class names are
          this component's private names — a caller overriding them is coupled to internals that can be
          renamed here without anything failing, and two callers had already started down that road.
        */
        &.hero {
          gap: 10px;
          padding: 52px 32px;
          border-width: 2px;
          border-color: var(--dark-gray);

        }

        &.error {
          border-color: var(--primary-red);
          background-color: rgba(213, 0, 0, 0.05);
          .upload { color: var(--primary-red); }
        }

        &.selected {
          border-color: var(--c-green);
          background-color: rgba(0, 170, 0, 0.05);
          .upload { color: var(--c-green); }
        }

        /* Blue, not the brand red: working is not an error, and red-while-scanning read as one. Blue is
           the one signal colour with no verdict attached — the rejected state already owns amber and the
           outcomes own green and red — and this token carries its own brighter dark-mode value. The soft
           blue tint was, in fact, what this state always had; only the border and icon disagreed. */
        &.state-uploading, &.state-scanning {
          border-color: var(--dashboard-blue);
          background-color: rgba(25, 118, 210, 0.05);
          .upload { color: var(--dashboard-blue); }
          .status-line { color: var(--dashboard-blue); }
        }

        &.state-ready {
          border-color: var(--c-green);
          background-color: rgba(0, 170, 0, 0.05);
          .upload { color: var(--c-green); }
          .state-ready-text { color: var(--c-green); font-weight: 600; }
        }

        &.state-infected, &.state-failed {
          border-color: var(--primary-red);
          background-color: rgba(213, 0, 0, 0.05);
          .upload { color: var(--primary-red); }
          .state-error-text { color: var(--primary-red); font-weight: 600; }
        }

        &.state-rejected {
          border-color: var(--c-amber, #f57c00);
          background-color: rgba(245, 124, 0, 0.05);
          .upload { color: var(--c-amber, #f57c00); }
          .state-error-text { color: var(--c-amber, #f57c00); font-weight: 600; }
        }

        .row-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
        }

        .row-text {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          min-width: 0;
          padding: 0 4px;
          overflow: hidden;
        }

        .row-action {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          min-width: 0;
        }

        .mat-icon.upload {
          font-size: 1.25rem;
          width: 1.25rem;
          height: 1.25rem;
          line-height: 1.25rem;
          color: var(--primary-color);
        }

        &.hero .mat-icon.upload {
          font-size: 30px;
          width: 30px;
          height: 30px;
          line-height: 30px;
          color: var(--color-on-surface-variant);
        }

        .upload-spinner {
          display: inline-block;
        }

        .hint-text {
          font-size: 0.85rem;
          color: var(--color-on-surface-variant, rgba(0,0,0,0.6));
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 100%;
        }

        .browse-link {
          color: var(--primary-color);
          text-decoration: underline;
          cursor: pointer;
        }

        .filename {
          font-size: 0.72rem;
          max-width: 100%;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
          color: var(--color-on-surface);
          display: block;
        }

        .status-line {
          font-size: 0.72rem;
          color: var(--color-on-surface);
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
          max-width: 100%;
        }

        .progress-bar {
          width: 80%;
          margin-top: 2px;
        }
      }
    }
  `],
  providers: [{
    provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => UploadSelectorComponent),
    multi: true
  }],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class UploadSelectorComponent implements OnDestroy, OnChanges, ControlValueAccessor {
  subs: Subscription[] = [];
  touched = false;
  id = `upload-selector-${Math.random().toString(36).substring(2, 15)}`;
  errorMessages = new Map();

  @Input() hasError = false;
  @Input() required = false;
  @Input() disable = false;

  /** Upload state from the parent UploaderComponent — drives border color, icon, and status text. */
  @Input() uploadState: 'idle' | 'uploading' | 'scanning' | 'ready' | 'infected' | 'rejected' | 'failed' = 'idle';
  /** Upload progress (0–100) shown as a progress bar when uploadState === 'uploading'. */
  @Input() progressPercent: number | null = null;
  /** Reason text shown when uploadState === 'rejected'. */
  @Input() statusReason: string | null = null;

  private _disabled = false;
  private _files: File[] = [];
  private _isDragOver = false;

  constructor(private cdr: ChangeDetectorRef) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Force re-render when upload state or progress changes from the parent
    if (changes['uploadState'] || changes['progressPercent'] || changes['statusReason']) {
      this.cdr.markForCheck();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private _onChange = (val: File[]) => {
  };
  private _onTouched = () => {
    this.touched = true;
  };

  @Input() accept = '*';

  @HostBinding('class.disabled')
  @Input()
  get disabled() {
    return this._disabled;
  }

  set disabled(val: boolean) {
    this._disabled = coerceBooleanProperty(val);
  }

  get files() {
    return this._files;
  }

  @HostBinding('class.empty-input')
  get isEmpty() {
    return !this.files?.length;
  }

  get isDragover() {
    return this._isDragOver;
  }

  set isDragover(value: boolean) {
    if (!this.disabled) {
      this._isDragOver = value;
    }
  }

  @Output() private valueChanged = new EventEmitter<File[]>();
  @ViewChild('fileInputEl') private fileInputEl: ElementRef;
  @Input() emptyPlaceholder = 'Drop file or click to select';

  /** Replaces the idle "Drop file or browse" prompt. Unset leaves the default. */
  @Input() promptTemplate?: TemplateRef<unknown>;

  /**
   * How much room the drop zone should take.
   *
   * `default` is the compact zone that sits inside a card or a form. `hero` is for a screen whose whole
   * subject is the upload, where the mockup asks for a taller box, a lighter rule and a bigger neutral icon.
   * Styling only — the states, validation and upload behaviour are identical.
   */
  @Input() variant: 'default' | 'hero' = 'default';

  writeValue(files: File[]): void {
    const fileArray = this.convertToArray(files);
    if (fileArray.length < 2) {
      this._files = fileArray;
      this.emitChanges(this._files);
      this.cdr.detectChanges();
    } else {
      alert('Multiple files not allowed. Please select one file for each type of required document.');
      throw Error('Multiple files not allowed');
    }

  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private emitChanges(files: File[]) {
    this.valueChanged.emit(files);
    this._onChange(files);
  }


  addFiles(files: File[] | FileList | File) {
    this._onTouched();
    const fileArray = this.convertToArray(files);

    // Validate that files are locally available (not cloud-only placeholders).
    // Cloud storage services (OneDrive, iCloud, Google Drive) can show files in the
    // file picker that haven't been downloaded yet. These files report a size but
    // cannot actually be read, causing the upload to fail with a broken connection.
    if (fileArray.length) {
      this.validateFilesReadable(fileArray).then(validFiles => {
        if (validFiles.length) {
          this.writeValue(validFiles);
        }
      });
    }
  }

  /**
   * Validates that selected files are actually readable (not cloud-only placeholders).
   * Attempts to read the first byte of each file. Cloud-synced files that haven't been
   * downloaded locally will fail to read, producing an error or an empty result.
   */
  private validateFilesReadable(files: File[]): Promise<File[]> {
    return Promise.all(
      files.map(file => {
        // Zero-byte files are always suspicious but could be legitimate empty files.
        // Try reading regardless — the FileReader will fail for cloud placeholders.
        return new Promise<File | null>(resolve => {
          const reader = new FileReader();
          reader.onloadend = () => {
            if (reader.result && (reader.result as ArrayBuffer).byteLength > 0) {
              resolve(file);
            } else {
              alert(
                `The file "${file.name}" could not be read. It may be stored in the cloud ` +
                '(OneDrive, iCloud, Google Drive) and not downloaded locally. ' +
                'Please download the file first, then try again.'
              );
              resolve(null);
            }
          };
          reader.onerror = () => {
            alert(
              `The file "${file.name}" could not be read. It may be stored in the cloud ` +
              '(OneDrive, iCloud, Google Drive) and not downloaded locally. ' +
              'Please download the file first, then try again.'
            );
            resolve(null);
          };
          // Read just the first 4 bytes to check if the file is accessible
          reader.readAsArrayBuffer(file.slice(0, 4));
        });
      })
    ).then(results => results.filter((f): f is File => f !== null));
  }

  removeFile(file: File) {
    const fileIndex = this.files.indexOf(file);
    if (fileIndex >= 0) {
      const currentFiles = this.files.slice();
      currentFiles.splice(fileIndex, 1);
      this.writeValue(currentFiles);
    }
  }

  clear() {
    this.writeValue([]);
    this.cdr.detectChanges();
  }

  @HostListener('change', ['$event'])
  change(event: Event) {
    event.stopPropagation();
    this._onTouched();
    const fileList: FileList = (event.target as HTMLInputElement).files;
    if (fileList?.length) {
      this.addFiles(fileList);
    } else {
      this.clear();
    }
    // clear it so change is triggered if same file is selected again
    (event.target as HTMLInputElement).value = '';
  }

  @HostListener('dragenter', ['$event'])
  @HostListener('dragover', ['$event'])
  activate(e) {
    e.preventDefault();
    this.isDragover = true;
  }

  @HostListener('dragleave', ['$event'])
  deactivate(e) {
    e.preventDefault();
    this.isDragover = false;
  }

  @HostListener('drop', ['$event'])
  handleDrop(e) {
    this.deactivate(e);
    if (!this.disabled) {

      const fileList = e.dataTransfer.files;
      this.removeDirectories(fileList).then((files: File[]) => {
        if (files?.length) {
          this.addFiles(files);
        }
        this._onTouched();
      });
    }
  }

  @HostListener('click')
  open() {
    if (!this.disabled) {
      this.fileInputEl?.nativeElement.click();
    }
  }

  public getFileName(file: File): string {
    const size = BytesPipe.transform(file.size, 2, 'B', 'MB');
    return `${file.name} (${size})`;
  }

  getDropzoneClass(): Record<string, boolean> {
    return {
      [`state-${this.uploadState}`]: this.uploadState !== 'idle',
      'error': this.uploadState === 'idle' && this.hasError,
      'selected': this.uploadState === 'idle' && this.files.length > 0 && !this.hasError,
      // Independent of state: a hero zone stays a hero zone while uploading, scanning and after selection.
      'hero': this.variant === 'hero',
    };
  }

  ngOnDestroy(): void {
    this.subs.forEach(x => x.unsubscribe());
  }

  private convertToArray(files: FileList | File[] | File | null | undefined): File[] {
    if (files) {
      if (files instanceof File) {
        return [files];
      } else if (Array.isArray(files)) {
        return files;
      } else {
        return Array.prototype.slice.call(files);
      }
    }
    return [];
  }

  private removeDirectories(files: FileList) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return new Promise((resolve, reject) => {
      const fileArray = this.convertToArray(files);
      const dirnames = [];
      const readerList = [];

      for (let i = 0; i < fileArray.length; i++) {
        const reader = new FileReader();

        reader.onerror = () => {
          dirnames.push(fileArray[i].name);
        };

        reader.onloadend = () => addToReaderList(i);

        reader.readAsArrayBuffer(fileArray[i]);
      }

      function addToReaderList(val: number) {
        readerList.push(val);
        if (readerList.length === fileArray.length) {
          resolve(fileArray.filter((file: File) => !dirnames.includes(file.name)));
        }
      }
    });
  }
}
